import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { crmOrganizationUnitDefaultListParams } from "../config";
import {
  createCrmOrganizationUnit,
  deleteCrmOrganizationUnit,
  fetchCrmOrganizationUnitCompleteList,
  fetchCrmOrganizationUnitList,
  fetchCrmOrganizationUnitTree,
  setCrmOrganizationUnitDisabled,
  updateCrmOrganizationUnit,
} from "../repo/crmOrganizationUnitRepo";
import {
  buildCrmOrganizationUnitDeleteBlockedReasons,
  buildCrmOrganizationUnitTreeTable,
  normalizeCrmOrganizationUnitFilters,
  normalizeCrmOrganizationUnitMutation,
} from "../service/crmOrganizationUnitService";
import type {
  CrmOrganizationUnitItem,
  CrmOrganizationUnitListFilters,
  CrmOrganizationUnitMutation,
  CrmOrganizationUnitTreeNode,
} from "../types";

const crmOrganizationUnitQueryRootKey = ["crm_organization_unit", "list"] as const;

export const emptyCrmOrganizationUnitMutation: CrmOrganizationUnitMutation = {
  orgId: 0,
  parentUnitId: undefined,
  unitType: "二级学院",
  unitName: "",
  orgStatus: "有效",
  effectiveFrom: "",
  effectiveTo: "",
  isDisabled: false,
  remark: "",
};

export type CrmOrganizationUnitDeletePreview = {
  item: CrmOrganizationUnitItem;
  blockedReasons: string[];
};

/** 管理组织单元列表、筛选、分页和节点维护行为。 */
export function useCrmOrganizationUnitRuntime() {
  const queryClient = useQueryClient();
  const [draftFilters, setDraftFilters] = useState<CrmOrganizationUnitListFilters>({});
  const [submittedFilters, setSubmittedFilters] = useState<CrmOrganizationUnitListFilters>({});
  const [page, setPage] = useState(crmOrganizationUnitDefaultListParams.page);
  const [pageSize, setPageSize] = useState(crmOrganizationUnitDefaultListParams.pageSize);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [deletePreview, setDeletePreview] = useState<CrmOrganizationUnitDeletePreview | null>(null);
  const [moveTargetItem, setMoveTargetItem] = useState<CrmOrganizationUnitItem | null>(null);

  const listParams = useMemo(
    () => ({
      ...crmOrganizationUnitDefaultListParams,
      ...submittedFilters,
      page,
      pageSize,
    }),
    [page, pageSize, submittedFilters],
  );
  const completeListParams = useMemo(
    () => ({
      ...crmOrganizationUnitDefaultListParams,
      ...submittedFilters,
      page: 1,
      pageSize: 100,
    }),
    [submittedFilters],
  );
  const isCompleteTreeMode = Boolean(
    submittedFilters.schoolKeyword &&
      !submittedFilters.unitKeyword &&
      !submittedFilters.unitType,
  );

  const listQuery = useQuery({
    queryKey: [...crmOrganizationUnitQueryRootKey, listParams],
    queryFn: () => fetchCrmOrganizationUnitList(listParams),
  });
  const completeListQuery = useQuery({
    queryKey: ["crm_organization_unit", "complete-list", completeListParams],
    queryFn: () => fetchCrmOrganizationUnitCompleteList(completeListParams),
    enabled: isCompleteTreeMode,
  });

  const listPage = listQuery.data ?? {
    items: [],
    page,
    pageSize,
    total: 0,
  };
  const completeListPage = completeListQuery.data ?? {
    items: [],
    page: 1,
    pageSize: 100,
    total: 0,
  };
  const displayPage = isCompleteTreeMode
    ? filterExactSchoolListPage(completeListPage, submittedFilters)
    : listPage;
  const treeTable = useMemo(
    () => buildCrmOrganizationUnitTreeTable(displayPage.items, submittedFilters),
    [displayPage.items, submittedFilters],
  );

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: crmOrganizationUnitQueryRootKey,
    });

  const createMutation = useMutation({
    mutationFn: (payload: CrmOrganizationUnitMutation) =>
      createCrmOrganizationUnit(normalizeCrmOrganizationUnitMutation(payload)),
    onSuccess: invalidateList,
  });
  const updateMutation = useMutation({
    mutationFn: ({
      item,
      payload,
    }: {
      item: CrmOrganizationUnitItem;
      payload: CrmOrganizationUnitMutation;
    }) => updateCrmOrganizationUnit(item.id, normalizeCrmOrganizationUnitMutation(payload)),
    onSuccess: invalidateList,
  });
  const disabledMutation = useMutation({
    mutationFn: (item: CrmOrganizationUnitItem) =>
      setCrmOrganizationUnitDisabled(item.id, !item.isDisabled),
    onSuccess: invalidateList,
  });
  const deleteMutation = useMutation({
    mutationFn: (item: CrmOrganizationUnitItem) => deleteCrmOrganizationUnit(item.id),
    onSuccess: invalidateList,
  });
  const moveTreeQuery = useQuery<CrmOrganizationUnitTreeNode>({
    queryKey: ["crm_organization_unit", "tree", moveTargetItem?.orgId],
    queryFn: () => fetchCrmOrganizationUnitTree(moveTargetItem?.orgId ?? 0),
    enabled: Boolean(moveTargetItem?.orgId),
  });

  return {
    draftFilters,
    items: displayPage.items,
    treeRows: treeTable.rows,
    defaultExpandedRowKeys: treeTable.expandedRowKeys,
    loading: listQuery.isLoading || (isCompleteTreeMode && completeListQuery.isLoading),
    isCompleteTreeMode,
    page,
    pageSize,
    total: displayPage.total,
    mutationErrorMessage,
    deletePreview,
    moveTree: moveTreeQuery.data,
    moveTreeLoading: moveTreeQuery.isLoading,
    moveTreeErrorMessage: moveTreeQuery.error ? "组织单元树加载失败，请稍后重试。" : "",
    saving:
      createMutation.isPending ||
      updateMutation.isPending ||
      disabledMutation.isPending ||
      deleteMutation.isPending,
    errorMessage: listQuery.error ? "组织单元列表加载失败，请稍后重试。" : "",
    setDraftFilters,
    setDeletePreview,
    setMoveTargetItem,
    clearMutationError() {
      setMutationErrorMessage("");
    },
    handleSearch() {
      setSubmittedFilters(normalizeCrmOrganizationUnitFilters(draftFilters));
      setPage(1);
    },
    handleReset() {
      setDraftFilters({});
      setSubmittedFilters({});
      setPage(1);
    },
    handlePageChange(nextPage: number) {
      setPage(Math.max(nextPage, 1));
    },
    handlePageSizeChange(nextPageSize: number) {
      setPageSize(nextPageSize);
      setPage(1);
    },
    async handleCreate(payload: CrmOrganizationUnitMutation) {
      await runMutation(() => createMutation.mutateAsync(payload), setMutationErrorMessage);
    },
    async handleUpdate(item: CrmOrganizationUnitItem, payload: CrmOrganizationUnitMutation) {
      await runMutation(() => updateMutation.mutateAsync({ item, payload }), setMutationErrorMessage);
    },
    async handleToggleDisabled(item: CrmOrganizationUnitItem) {
      await runMutation(() => disabledMutation.mutateAsync(item), setMutationErrorMessage);
    },
    handleRequestDelete(item: CrmOrganizationUnitItem) {
      setDeletePreview({
        item,
        blockedReasons: buildCrmOrganizationUnitDeleteBlockedReasons(item),
      });
    },
    async handleConfirmDelete() {
      if (!deletePreview || deletePreview.blockedReasons.length > 0) {
        return;
      }
      await runMutation(
        () => deleteMutation.mutateAsync(deletePreview.item),
        setMutationErrorMessage,
      );
      setDeletePreview(null);
    },
  };
}

/** 执行变更请求并统一记录保存失败提示。 */
async function runMutation<T>(
  execute: () => Promise<T>,
  setMutationErrorMessage: (message: string) => void,
) {
  setMutationErrorMessage("");
  try {
    return await execute();
  } catch (error) {
    setMutationErrorMessage("保存失败，请检查填写内容后重试。");
    throw error;
  }
}

/** 学校完整树优先展示精确命中的学校，避免“中国美术学院”混入“中国美术学院上海设计学院”。 */
function filterExactSchoolListPage(
  listPage: {
    items: CrmOrganizationUnitItem[];
    page: number;
    pageSize: number;
    total: number;
  },
  filters: CrmOrganizationUnitListFilters,
) {
  const schoolKeyword = filters.schoolKeyword?.trim();
  if (!schoolKeyword || listPage.items.length === 0) {
    return listPage;
  }

  const exactSchoolIds = new Set(
    listPage.items
      .filter((item) => item.schoolName.trim() === schoolKeyword)
      .map((item) => item.orgId),
  );
  const exactSpellCodeIds =
    exactSchoolIds.size > 0
      ? exactSchoolIds
      : new Set(
          listPage.items
            .filter((item) => item.schoolSpellCode.trim().toLowerCase() === schoolKeyword.toLowerCase())
            .map((item) => item.orgId),
        );
  if (exactSpellCodeIds.size === 0) {
    return listPage;
  }

  const items = listPage.items.filter((item) => exactSpellCodeIds.has(item.orgId));
  return {
    ...listPage,
    items,
    total: items.length,
  };
}
